import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ImportJobItem, ImportMappingInput, ImportProcessingStatus, ImportRowItem, ImportTarget, ImportUploadResult, Paginated } from '@crm/shared';
import { api } from '../../api/client';

export const useImports = () =>
  useQuery({
    queryKey: ['imports'],
    queryFn: async () => (await api.get<ImportJobItem[]>('/imports')).data,
    refetchInterval: (q) => (q.state.data?.some((j) => j.status === 'PROCESSING') ? 3_000 : false),
  });
export function useImport(id: string | undefined) {
  return useQuery({
    queryKey: ['import', id],
    queryFn: async () => (await api.get<ImportJobItem>(`/imports/${id}`)).data,
    enabled: !!id,
    // poll while running, and while a pause request is still being honoured (paused with a heartbeat)
    refetchInterval: (q) => (q.state.data?.status === 'PROCESSING' || (q.state.data?.status === 'PAUSED' && q.state.data.heartbeatAt) ? 2_000 : false),
  });
}
/** Worker liveness and running imports; polled while an import is processing. */
export function useImportProcessingStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['import-status'],
    queryFn: async () => (await api.get<ImportProcessingStatus>('/imports/status')).data,
    enabled,
    refetchInterval: enabled ? 5_000 : false,
  });
}
export function useImportTargets(objectType: 'CONTACT' | 'ORGANIZATION') {
  return useQuery({ queryKey: ['import-targets', objectType], queryFn: async () => (await api.get<ImportTarget[]>('/imports/targets', { params: { objectType } })).data });
}
export function useImportRows(id: string | undefined, result: string, page: number, version?: string) {
  return useQuery({
    queryKey: ['import-rows', id, result, page, version],
    queryFn: async () => (await api.get<Paginated<ImportRowItem>>(`/imports/${id}/rows`, { params: { result, page, pageSize: 25 } })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

const KEYS = [['imports'], ['import'], ['import-targets'], ['fields'], ['segments'], ['contacts'], ['organizations'], ['tags'], ['lists'], ['dashboard']];
function useInvalidate() {
  const qc = useQueryClient();
  return () => KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}
export function useUploadImport() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async ({ file, objectType, onProgress }: { file: File; objectType: 'CONTACT' | 'ORGANIZATION'; onProgress?: (percent: number) => void }) => {
      const form = new FormData();
      form.append('file', file);
      return (
        await api.post<ImportUploadResult>('/imports/upload', form, {
          params: { objectType },
          headers: { 'Content-Type': 'multipart/form-data' },
          // reading a large workbook takes a while once it is uploaded; fail instead of spinning forever
          timeout: 5 * 60_000,
          onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
        })
      ).data;
    },
    onSuccess: inv,
  });
}
export function useSetImportMapping() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async ({ id, ...input }: ImportMappingInput & { id: string }) => (await api.post<ImportJobItem>(`/imports/${id}/mapping`, input)).data, onSuccess: inv });
}
export function useRerunImport() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async ({ id, scope }: { id: string; scope: 'all' | 'problems' }) => (await api.post<ImportJobItem>(`/imports/${id}/rerun`, { scope })).data, onSuccess: inv });
}
export function usePauseImport() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<ImportJobItem>(`/imports/${id}/pause`)).data, onSuccess: inv });
}
export function useResumeImport() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<ImportJobItem>(`/imports/${id}/resume`)).data, onSuccess: inv });
}
export function useStartImport() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.post<ImportJobItem>(`/imports/${id}/start`)).data, onSuccess: inv });
}
export function useDeleteImport() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/imports/${id}`)).data, onSuccess: inv });
}
